/* eslint-disable react-hooks/exhaustive-deps */
import { IPageRequest } from 'common/interfaces/IPageRequest';
import { IPageResponse } from 'common/interfaces/IPageResponse';
import { CustomerApplicationsSearchBar } from 'components/CustomerApplications/CustomerApplicationsSearchBar';
import { CustomerApplicationsTable } from 'components/CustomerApplications/CustomerApplicationsTable';
import { CancelCustomerApplicationConfirmModal } from 'components/CustomerApplications/modals/CancelCustomerApplicationConfirmModal';
import { CreditApplicationModal } from 'components/CustomerCards/modals/CreditApplicationModal';
import { ModifyCustomerCardModal } from 'components/CustomerCards/modals/ModifyCustomerCard/ModifyCustomerCardModal';
import { IncreaseGeneralCreditModal } from 'components/Dashboard/modal/IncreaseGeneralCreditModal';
import { BlockingApplication, blockingApplicationNotice, CustomerApplication, CustomerApplicationFilters, CustomerApplicationType } from 'models/customer-application';
import { ICustomerCardType } from 'models/customer-card';
import { PageProps } from 'models/page';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RequestCardButton } from 'components/CustomerCards/CustomerCardsSearchBar';
import { CARDS_PAGE_TITLE } from 'common/constants';
import { CustomerPermission, InitContextProps, useAuthContext } from 'services/context/authentication.context';
import { InfoCard } from 'components/InfoCard/InfoCard';
import CustomerApplicationService from 'services/customer-application.service';

export function CustomerApplications(props: PageProps) {
    const { state, dispatch, hasPermission }: InitContextProps = useAuthContext();
    const { customerId, customer } = state;
    const history = useHistory();

    const [applications, setApplications] = useState<IPageResponse<CustomerApplication> | null>(null);
    const [pageRequest, setPageRequest] = useState<IPageRequest>({
      page: 0,
      size: 20
    });
    const [filters, setFilters] = useState<CustomerApplicationFilters>({});
    const [isRequesting, setIsRequesting] = useState(false);
    const [applicationToCancel, setApplicationToCancel] = useState<number>();
    const [applicationToUpdate, setApplicationToUpdate] = useState<CustomerApplication>();

    const findApplications = async (pageRequest: IPageRequest, filters: CustomerApplicationFilters) => {
         try {
          setIsRequesting(true);

          const res = await CustomerApplicationService.getPaged(
            customerId!,
            {
              ...pageRequest,
              ...filters
            }
          );

          setApplications(res);
         } catch(e) {
          console.error(e);
         } finally {
          setIsRequesting(false);
         }
    };

    useEffect(() => {
          props.onLoadValidatePage();
          findApplications(pageRequest, filters);
    }, []);

    const onPageChange = (targetPage: number) => {
        if (pageRequest.page !== targetPage - 1) {
            const req = {
              ...pageRequest,
                page: targetPage - 1
            };

            setPageRequest(req);
            findApplications(req, filters);
        }
    };

    const handleFilterChange = (type: CustomerApplicationType) => {
        const pageReq = {
            ...pageRequest,
            page: 0
        }
        const filter = { type }

        setPageRequest(pageReq)
        setFilters(filter)
        findApplications(pageReq, filter)
    };

    const cancelApplication = async () => {
      try {
        await CustomerApplicationService.cancel(
          applicationToCancel!,
          customerId!,
          customer.contactId!
        );
        
        findApplications(pageRequest, filters);

        // Which types the cancelled application was blocking is the server's call, so drop the local map and
        // let the customer refresh restore it.
        dispatch({
          type: 'setCustomer',
          value: {
            ...customer,
            blockingApplications: {}
          }
        });

        setApplicationToCancel(undefined);
        toast('Taotlus tühistatud', { type: 'success' });
      } catch(e) {
        console.error(e)
        toast('Taotlus tühistamine ebaõnnestus', { type: 'error' });
      }
    }

    const hideEditModal = () => {
      setApplicationToUpdate(undefined);
    }

    const refresh = () => {
      findApplications(pageRequest, filters);
    }

    // The button routes to the cards page, which offers both card types, so it only closes when neither is available.
    const cardApplicationBlockers: (BlockingApplication | undefined)[] = [
      CustomerApplicationType.PAYMENT_CARD_APPLICATION,
      CustomerApplicationType.DISCOUNT_CARD_APPLICATION
    ].map(type => customer?.blockingApplications?.[type]);

    const blockingCardApplications = cardApplicationBlockers.every(blocker => blocker != null)
      ? cardApplicationBlockers as BlockingApplication[]
      : [];

    const isCardApplication = !!applicationToUpdate && 
      (applicationToUpdate.applicationType === CustomerApplicationType.PAYMENT_CARD_APPLICATION || applicationToUpdate.applicationType === CustomerApplicationType.DISCOUNT_CARD_APPLICATION);
    
    return (
      <>
        <div className="customer-applications__container">
          <CustomerApplicationsSearchBar
            onTypeChange={handleFilterChange}
            type={filters.type}
            requestButton={
              hasPermission(CustomerPermission.WEB_ADMIN) && (
                <RequestCardButton
                  title="Telli uus kaart"
                  onClick={() => history.push('/' + CARDS_PAGE_TITLE)}
                  disabled={blockingCardApplications.length > 0}
                />
              )
            }
          />
          {
            hasPermission(CustomerPermission.WEB_ADMIN) && blockingCardApplications.length > 0 && (
              <InfoCard
                text={blockingApplicationNotice(blockingCardApplications)}
                variant='warning'
                className='info-card--under-search-bar align-self-end mb-3'
              />
            )
          }
          <CustomerApplicationsTable
            applications={applications}
            isRequesting={isRequesting}
            onPageChange={onPageChange}
            onCancel={(id) => setApplicationToCancel(id)}
            onUpdate={(application) => setApplicationToUpdate(application)}
          />
        </div>
        {
          applicationToCancel && (
            <CancelCustomerApplicationConfirmModal 
              cancel={() => setApplicationToCancel(undefined)}
              confirm={cancelApplication}
            />
          )
        }
        {
            applicationToUpdate && isCardApplication &&
            <ModifyCustomerCardModal
                onSuccess={refresh}
                handleModalHide={hideEditModal}
                selectedCardType={
                  applicationToUpdate.applicationType === CustomerApplicationType.PAYMENT_CARD_APPLICATION 
                    ? ICustomerCardType.PAYMENT_CARD : ICustomerCardType.DISCOUNT_CARD
                } 
                application={applicationToUpdate}
            />    
        }
        {
            applicationToUpdate && applicationToUpdate.applicationType === CustomerApplicationType.INCREASE_CREDIT_APPLICATION &&
            <IncreaseGeneralCreditModal
                onSuccess={refresh}
                handleModalHide={hideEditModal}
                application={applicationToUpdate}
            />    
        }
        {
            applicationToUpdate && applicationToUpdate.applicationType === CustomerApplicationType.CREDIT_APPLICATION &&
            <CreditApplicationModal
                onSuccess={refresh}
                handleModalHide={hideEditModal}
                application={applicationToUpdate}
            />    
        }
      </>
    )
}